import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  Layers,
  MoreHorizontal,
  Pencil,
  Trash2,
  TriangleAlert,
  Zap,
} from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { EmptyState } from '@/components/ui/empty-state'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Heading } from '@/components/ui/typography'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ActionSearchBar } from '@/components/actions/ActionSearchBar'
import { actionsApiClient } from '@/lib/api/actions.client'
import { actionsCatalogApiClient } from '@/lib/api/actions-catalog.client'
import {
  buildCatalogDeleteMessage,
  describeAction,
  directoryLookupFrom,
  searchHaystackForAction,
  type DirectoryLookup,
} from '@/lib/actions/describe'
import { pathFor, ROUTE_REGISTRY } from '@/lib/routes'
import { confirmDialog } from '@/lib/utils/window-utils'
import { useAppAuth } from '@/contexts/app-auth.context'
import { DirectoryProvider, useDirectory } from '@/contexts/directory.context'
import { useDocumentHead } from '@/hooks/use-document-head'
import type {
  NodeActionGroup,
  WorkflowActionCatalogEntry,
} from '@super/shared-types'

interface GroupRow extends NodeActionGroup {
  usageCount: number | null
}

interface CatalogRow extends WorkflowActionCatalogEntry {
  usageCount: number | null
  emptyAfterCount: number
}

type ActionsTab = 'actions' | 'groups'

function matchesGroup(
  group: NodeActionGroup,
  needle: string,
  directory: DirectoryLookup | undefined,
): boolean {
  if (!needle) return true
  const parts: string[] = [group.name ?? '', group.description ?? '']
  for (const member of group.members ?? []) {
    if (member.workflowAction) {
      parts.push(
        searchHaystackForAction(
          {
            ...(member.workflowAction as unknown as WorkflowActionCatalogEntry),
            contacts: member.workflowAction.contacts ?? [],
          },
          directory,
        ),
      )
    }
  }
  return parts.join(' ').toLowerCase().includes(needle)
}

function matchesAction(
  entry: WorkflowActionCatalogEntry,
  needle: string,
  directory: DirectoryLookup | undefined,
): boolean {
  if (!needle) return true
  return searchHaystackForAction(entry, directory).includes(needle)
}

export function ActionsPage() {
  return (
    <DirectoryProvider>
      <ActionsPageContent />
    </DirectoryProvider>
  )
}

function ActionsPageContent() {
  useDocumentHead({ title: 'Actions' })
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const tab: ActionsTab =
    searchParams.get('tab') === 'groups' ? 'groups' : 'actions'
  const selectTab = (next: string) => {
    setSearchParams(
      prev => {
        const params = new URLSearchParams(prev)
        params.set('tab', next)
        return params
      },
      { replace: true },
    )
  }
  const { isSuperAdmin, currentCompany } = useAppAuth()
  const { roles, staff } = useDirectory()
  const directoryLookup = useMemo(
    () => directoryLookupFrom(roles, staff),
    [roles, staff],
  )
  const [catalog, setCatalog] = useState<CatalogRow[]>([])
  const [groups, setGroups] = useState<GroupRow[]>([])
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')
  const needle = query.trim().toLowerCase()

  const filteredCatalog = useMemo(
    () =>
      catalog.filter(entry => matchesAction(entry, needle, directoryLookup)),
    [catalog, needle, directoryLookup],
  )
  const filteredGroups = useMemo(
    () => groups.filter(group => matchesGroup(group, needle, directoryLookup)),
    [groups, needle, directoryLookup],
  )

  const load = useCallback(async () => {
    setLoading(true)
    const scoped = isSuperAdmin && currentCompany
    const [catalogList, groupList] = await Promise.all([
      scoped
        ? actionsCatalogApiClient.getActionsForCompanyByAdmin(
            currentCompany!.id,
          )
        : actionsCatalogApiClient.getActions(),
      scoped
        ? actionsApiClient.getActionsForCompanyByAdmin(currentCompany!.id)
        : actionsApiClient.getActions(),
    ])
    const catalogIds = catalogList.map(e => e.id)
    const groupIds = groupList.map(g => g.id)
    const [catalogUsage, groupUsage] = await Promise.all([
      scoped
        ? actionsCatalogApiClient.getActionUsageCountsForCompanyByAdmin(
            catalogIds,
            currentCompany!.id,
          )
        : actionsCatalogApiClient.getActionUsageCounts(catalogIds),
      scoped
        ? actionsApiClient.getActionUsageCountsForCompanyByAdmin(
            groupIds,
            currentCompany!.id,
          )
        : actionsApiClient.getActionUsageCounts(groupIds),
    ])
    const catalogWithUsage = catalogList
      .filter(entry => entry.actionType !== 'PHONE_CALL')
      .map(entry => ({
        ...entry,
        usageCount: catalogUsage[entry.id] ?? 0,
        emptyAfterCount: 0,
      }))
    const groupsWithUsage = groupList.map(group => ({
      ...group,
      usageCount: groupUsage[group.id] ?? 0,
    }))
    setCatalog(catalogWithUsage)
    setGroups(groupsWithUsage)
    setLoading(false)
  }, [isSuperAdmin, currentCompany])

  useEffect(() => {
    void load()
  }, [load])

  async function handleDeleteCatalogAction(entry: CatalogRow) {
    const label = entry.resolvedName ?? 'Untitled'
    const usage =
      isSuperAdmin && currentCompany
        ? await actionsCatalogApiClient.getActionUsageForCompanyByAdmin(
            entry.id,
            currentCompany.id,
          )
        : await actionsCatalogApiClient.getActionUsage(entry.id)
    const confirmed = await confirmDialog(
      buildCatalogDeleteMessage(
        label,
        usage?.count ?? 0,
        usage?.emptyAfterCount ?? 0,
      ),
    )
    if (!confirmed) return
    const result =
      isSuperAdmin && currentCompany
        ? await actionsCatalogApiClient.deleteActionForCompany(
            entry.id,
            currentCompany.id,
          )
        : await actionsCatalogApiClient.deleteAction(entry.id)
    if (result.ok) {
      toast.success('Action deleted')
      void load()
      return
    }
    toast.error('Could not delete the action')
  }

  async function handleDeleteGroup(group: GroupRow) {
    const label = group.name ?? 'Untitled'
    const usage = group.usageCount ?? 0
    const message =
      usage === 0
        ? `Delete "${label}"? It isn't referenced by any graph.`
        : `Delete "${label}"? ${usage} graph${usage === 1 ? '' : 's'} will break.`
    const confirmed = await confirmDialog(message)
    if (!confirmed) return
    const ok =
      isSuperAdmin && currentCompany
        ? await actionsApiClient.deleteActionForCompany(
            group.id,
            currentCompany.id,
          )
        : await actionsApiClient.deleteAction(group.id)
    if (ok) {
      toast.success('Group deleted')
      void load()
    } else {
      toast.error('Could not delete the group')
    }
  }

  return (
    <div className="h-full overflow-y-auto pt-16">
      <div className="space-y-10 p-6 pt-0 pb-24">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mt-6">
          <PageHeader title="Actions" />
        </div>

        <ActionSearchBar value={query} onChange={setQuery} />

        <Tabs value={tab} onValueChange={selectTab} className="w-full">
          <TabsList>
            <TabsTrigger value="actions">
              Actions{loading ? '' : ` (${filteredCatalog.length})`}
            </TabsTrigger>
            <TabsTrigger value="groups">
              Groups{loading ? '' : ` (${filteredGroups.length})`}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="actions" className="mt-6">
            <CatalogSection
              loading={loading}
              rows={filteredCatalog}
              hasFilter={needle.length > 0}
              directory={directoryLookup}
              onNew={() =>
                navigate(pathFor(ROUTE_REGISTRY.NODE_ACTIONS_NEW.id))
              }
              onEdit={entry =>
                navigate(
                  pathFor(ROUTE_REGISTRY.NODE_ACTIONS_DETAIL.id, {
                    id: entry.id,
                  }),
                )
              }
              onDelete={handleDeleteCatalogAction}
            />
          </TabsContent>
          <TabsContent value="groups" className="mt-6">
            <GroupsSection
              loading={loading}
              rows={filteredGroups}
              hasFilter={needle.length > 0}
              onNew={() =>
                navigate(pathFor(ROUTE_REGISTRY.NODE_ACTION_GROUPS_NEW.id))
              }
              onEdit={group =>
                navigate(
                  pathFor(ROUTE_REGISTRY.NODE_ACTION_GROUPS_DETAIL.id, {
                    id: group.id,
                  }),
                )
              }
              onDelete={handleDeleteGroup}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}

interface CatalogSectionProps {
  loading: boolean
  rows: CatalogRow[]
  hasFilter: boolean
  directory: DirectoryLookup
  onNew: () => void
  onEdit: (entry: CatalogRow) => void
  onDelete: (entry: CatalogRow) => void
}

function CatalogSection({
  loading,
  rows,
  hasFilter,
  directory,
  onNew,
  onEdit,
  onDelete,
}: CatalogSectionProps) {
  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Heading as="h3" className="text-base">
            Actions
          </Heading>
          <p className="text-sm text-muted-foreground">
            Individual actions — pull them into a group or use them directly on
            a node.
          </p>
        </div>
        <Button
          onClick={onNew}
          variant="form"
          size="sm"
          className="h-9 w-fit shrink-0"
        >
          New action
        </Button>
      </div>

      {loading ? (
        <SectionLoading />
      ) : rows.length === 0 ? (
        hasFilter ? (
          <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
            No actions match this search.
          </div>
        ) : (
          <EmptyState
            icon={<Zap className="h-8 w-8 text-muted-foreground" />}
            title="No actions yet"
            description="Create a Transfer, a notification, or a push — once and reusable anywhere."
            actionLabel="New action"
            onAction={onNew}
          />
        )
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Used in</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead className="w-[100px]" align="center">
                  Options
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(entry => (
                <TableRow
                  key={entry.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => onEdit(entry)}
                >
                  <TableCell>
                    {entry.resolvedName ? (
                      <>
                        <div className="font-medium">{entry.resolvedName}</div>
                        <div className="text-sm text-muted-foreground truncate max-w-md">
                          {describeAction(entry, directory)}
                        </div>
                      </>
                    ) : (
                      <div className="font-medium">
                        {describeAction(entry, directory)}
                      </div>
                    )}
                    {entry.description && (
                      <div className="text-sm text-muted-foreground truncate max-w-md">
                        {entry.description}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">
                      {entry.actionType.replace(/_/g, ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        (entry.usageCount ?? 0) > 0 ? 'default' : 'outline'
                      }
                    >
                      {entry.usageCount ?? 0} group
                      {entry.usageCount === 1 ? '' : 's'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {new Date(entry.updatedAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={e => e.stopPropagation()}
                        >
                          <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem
                          onClick={e => {
                            e.stopPropagation()
                            onEdit(entry)
                          }}
                        >
                          <Pencil className="w-4 h-4 mr-2" />
                          Edit action
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={e => {
                            e.stopPropagation()
                            void onDelete(entry)
                          }}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete action
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  )
}

interface GroupsSectionProps {
  loading: boolean
  rows: GroupRow[]
  hasFilter: boolean
  onNew: () => void
  onEdit: (group: GroupRow) => void
  onDelete: (group: GroupRow) => void
}

function GroupsSection({
  loading,
  rows,
  hasFilter,
  onNew,
  onEdit,
  onDelete,
}: GroupsSectionProps) {
  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Heading as="h3" className="text-base">
            Action groups
          </Heading>
          <p className="text-sm text-muted-foreground">
            Ordered collections of actions — the agent runs each in sequence and
            routes on the outcome.
          </p>
        </div>
        <Button
          onClick={onNew}
          variant="form"
          size="sm"
          className="h-9 w-fit shrink-0"
        >
          New group
        </Button>
      </div>

      {loading ? (
        <SectionLoading />
      ) : rows.length === 0 ? (
        hasFilter ? (
          <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
            No groups match this search.
          </div>
        ) : (
          <EmptyState
            icon={<Layers className="h-8 w-8 text-muted-foreground" />}
            title="No groups yet"
            description="Compose a group from one or more catalog actions, then reference it from a node."
            actionLabel="New group"
            onAction={onNew}
          />
        )
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Actions</TableHead>
                <TableHead>Types</TableHead>
                <TableHead>Used in</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead className="w-[100px]" align="center">
                  Options
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(group => {
                const count = group.members?.length ?? 0
                const types = Array.from(
                  new Set(
                    (group.members ?? [])
                      .map(m => m.workflowAction?.actionType)
                      .filter(Boolean) as string[],
                  ),
                )
                return (
                  <TableRow
                    key={group.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => onEdit(group)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2 font-medium">
                        {count === 0 && (
                          <TriangleAlert
                            className="h-4 w-4 shrink-0 text-amber-500"
                            aria-label="Empty group"
                          />
                        )}
                        <span>{group.name ?? 'Untitled'}</span>
                      </div>
                      {group.description && (
                        <div className="text-sm text-muted-foreground truncate max-w-md">
                          {group.description}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          count > 0
                            ? 'default'
                            : ('warning' as unknown as 'default')
                        }
                        className={
                          count === 0
                            ? 'bg-amber-100 text-amber-900 hover:bg-amber-100 border-amber-200'
                            : undefined
                        }
                      >
                        {count} action{count === 1 ? '' : 's'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {types.length === 0 ? (
                          <span className="text-xs text-muted-foreground">
                            —
                          </span>
                        ) : (
                          types.map(type => (
                            <Badge key={type} variant="secondary">
                              {type.replace(/_/g, ' ')}
                            </Badge>
                          ))
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          (group.usageCount ?? 0) > 0 ? 'default' : 'outline'
                        }
                      >
                        {group.usageCount ?? 0} graph
                        {group.usageCount === 1 ? '' : 's'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {new Date(group.updatedAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={e => e.stopPropagation()}
                          >
                            <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem
                            onClick={e => {
                              e.stopPropagation()
                              onEdit(group)
                            }}
                          >
                            <Pencil className="w-4 h-4 mr-2" />
                            Edit group
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={e => {
                              e.stopPropagation()
                              void onDelete(group)
                            }}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete group
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  )
}

function SectionLoading() {
  return (
    <div className="space-y-2">
      {[1, 2, 3].map(i => (
        <Skeleton key={i} className="h-14 w-full" />
      ))}
    </div>
  )
}
